import { useContext } from 'react';
import { AuthContext } from '../context/Auth/AuthContext';

/**
 * Verifica se o usuário logado possui uma permission key RBAC.
 *
 * ESCOPO DESTE HOOK:
 * Controla o que o ADMIN da empresa libera para seus USUÁRIOS.
 * Não substitui e não interfere com restrições de plano (PlanPermissionsContext).
 *
 * Hierarquia de acesso:
 *   super admin (user.super)   → bypassa tudo, sempre allowed
 *   owner (user.isOwner)       → dono da empresa, bypassa RBAC, sempre allowed
 *   admin (profile: "admin")   → respeita RBAC (role + overrides), como qualquer usuário
 *   user (profile: "user")     → verifica user.permissions[] injetado pelo backend
 *
 * Para funcionalidades restritas por plano (campanhas, IA, WhatsApp Oficial, etc.),
 * use usePlanPermissions() ou usePageAccess({ planFeature }) — não este hook.
 *
 * @param {string} key — permission key no padrão "recurso:acao" (ex: "tickets:read")
 * @returns {{ allowed: boolean }}
 *
 * @example — esconder componente
 * const { allowed } = usePermission('reports:read');
 * if (!allowed) return null;
 *
 * @example — desabilitar botão
 * const { allowed } = usePermission('contacts:delete');
 * <Button disabled={!allowed}>Excluir</Button>
 *
 * @example — combinar com restrição de plano
 * const { allowed: hasPlan } = usePlanPermissions();   // plano libera campaigns
 * const { allowed: hasRbac } = usePermission('campaigns:read'); // admin liberou pro user
 * if (!hasPlan || !hasRbac) return null;
 */
export const usePermission = (key) => {
  const { user, loading } = useContext(AuthContext);

  // Enquanto o auth está carregando, não bloqueia — evita ForbiddenPage/redirect com cache stale
  if (loading) return { allowed: true, loading: true };

  if (!user) return { allowed: false, loading: false };
  if (user.super) return { allowed: true, loading: false };
  if (user.isOwner) return { allowed: true, loading: false };

  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  return { allowed: permissions.includes(key), loading: false };
};

export default usePermission;
